import React from 'react';
import { logger } from '@/utils/logger';
import { supabase } from '@/lib/supabase';
import { BarChart, DollarSign, Package, Users } from 'lucide-react';

interface RecentOrder {
  id: string;
  created_at: string;
  status: string;
  total_amount: number;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

interface ProductSale {
  product_id: string;
  product_name: string;
  quantity: number;
  price: number;
  total: number;
  delivery_dates: string[]; // All delivery dates this product appears in
  quantities_by_date: Record<string, number>; // Track quantity for each date
  totals_by_date: Record<string, number>; // Track revenue for each date
}

interface ProducerOrdersInfo {
  producer_id: string;
  producer_name: string;
  total_orders: number;
  total_revenue: number;
  last_order_date: string | null;
  products: ProductSale[];
}

// New interfaces for delivery date organization
interface ProducerSaleSummary {
  producer_id: string;
  producer_name: string;
  total_amount: number;
}

interface DeliveryDateSales {
  date: string;
  producers: ProducerSaleSummary[];
  total_amount: number;
}

export function Dashboard() {
  const [stats, setStats] = React.useState({
    totalProducts: 0,
    totalProducers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    producerOrders: [] as ProducerOrdersInfo[],
    salesByDeliveryDate: [] as DeliveryDateSales[]
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [errorDetails, setErrorDetails] = React.useState<any>(null);
  const [selectedDate, setSelectedDate] = React.useState<string | 'all'>('all');
  const [availableDates, setAvailableDates] = React.useState<string[]>([]);

  // Function to fetch dashboard stats
  const fetchStats = React.useCallback(async () => {
    try {
      logger.debug("Starting to fetch dashboard stats...");
      
      // Fetch products count
      const { count: productsCount, error: productsError } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true });
      
      if (productsError) {
        logger.error("Error fetching products count:", productsError);
      }
      
      // Fetch producers
      const { data: producers, error: producersError } = await supabase
        .from('producers')
        .select('id, company_name');
      
      if (producersError) {
        logger.error("Error fetching producers:", producersError);
        throw producersError;
      }
      
      // Fetch orders
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('id, created_at, delivery_date, total_amount');
      
      if (ordersError) {
        logger.error("Error fetching orders:", ordersError);
        throw ordersError;
      }
      
      // Fetch all products
      const { data: products, error: productsListError } = await supabase
        .from('products')
        .select('id, name, producer_id');
      
      if (productsListError) {
        logger.error("Error fetching products list:", productsListError);
        throw productsListError;
      }
      
      // Fetch order items
      const { data: orderItems, error: orderItemsError } = await supabase
        .from('order_items')
        .select('id, order_id, product_id, quantity, unit_price, price_at_time');
      
      if (orderItemsError) {
        logger.error("Error fetching order items:", orderItemsError);
        throw orderItemsError;
      }
      
      // Create lookup maps for more efficient processing
      const producerMap = new Map();
      if (producers) {
        producers.forEach(producer => {
          producerMap.set(producer.id, {
            producer_id: producer.id,
            producer_name: producer.company_name,
            total_orders: 0,
            total_revenue: 0,
            last_order_date: null,
            products: [] as ProductSale[]
          });
        });
      }
      
      const productMap = new Map();
      if (products) {
        products.forEach(product => {
          productMap.set(product.id, {
            name: product.name,
            producer_id: product.producer_id
          });
        });
      }
      
      const orderMap = new Map();
      if (orders) {
        orders.forEach(order => {
          orderMap.set(order.id, {
            created_at: order.created_at,
            delivery_date: order.delivery_date,
            total_amount: order.total_amount
          });
        });
      }
      
      // Create a structure to track products by order item, preventing double-counting
      const producerOrdersData = new Map(producerMap);
      const processedOrders = new Set(); // Track unique orders per producer
      const producerProductsMap = new Map(); // Track products by producer to prevent aggregation issues
      const deliveryDateMap = new Map<string, DeliveryDateSales>();
      const allDeliveryDates = new Set<string>(); // Track all unique delivery dates
      
      // Process each order item exactly once
      if (orderItems) {
        orderItems.forEach(item => {
          const product = productMap.get(item.product_id);
          const order = orderMap.get(item.order_id);
          
          if (product && order) {
            const producerId = product.producer_id;
            const producer = producerOrdersData.get(producerId);
            // Use price_at_time if available, otherwise fall back to unit_price
            const itemPrice = item.price_at_time || item.unit_price;
            const revenue = item.quantity * itemPrice;
            
            if (producer) {
              // Update producer order stats
              if (!processedOrders.has(`${producerId}-${item.order_id}`)) {
                producer.total_orders++;
                processedOrders.add(`${producerId}-${item.order_id}`);
                
                if (!producer.last_order_date || new Date(order.created_at) > new Date(producer.last_order_date)) {
                  producer.last_order_date = order.created_at;
                }
              }
              
              producer.total_revenue += revenue;
              
              // Get the delivery date for this order
              const deliveryDate = order.delivery_date || order.created_at.split('T')[0];
              const dateKey = new Date(deliveryDate).toISOString().split('T')[0];
              
              // Add to our collection of all delivery dates
              allDeliveryDates.add(dateKey);
              
              // Create a unique key for this producer's product
              const producerProductKey = `${producerId}:${item.product_id}`;
              
              // If we haven't seen this producer-product combination yet, initialize it
              if (!producerProductsMap.has(producerProductKey)) {
                producerProductsMap.set(producerProductKey, {
                  product_id: item.product_id,
                  product_name: product.name,
                  quantity: 0,
                  price: itemPrice,
                  total: 0,
                  delivery_dates: [], // Initialize empty array for delivery dates
                  quantities_by_date: {}, // Initialize empty object for quantity by date
                  totals_by_date: {} // Initialize empty object for total by date
                });
              }
              
              // Get the product entry and update its values
              const productEntry = producerProductsMap.get(producerProductKey);
              productEntry.quantity += item.quantity;
              productEntry.total += revenue;
              
              // Add this delivery date to the product if not already included
              if (!productEntry.delivery_dates.includes(dateKey)) {
                productEntry.delivery_dates.push(dateKey);
              }
              
              // Track quantity and total by delivery date
              if (!productEntry.quantities_by_date[dateKey]) {
                productEntry.quantities_by_date[dateKey] = 0;
                productEntry.totals_by_date[dateKey] = 0;
              }
              productEntry.quantities_by_date[dateKey] += item.quantity;
              productEntry.totals_by_date[dateKey] += revenue;
              
              // Create or update the delivery date sales entry
              if (!deliveryDateMap.has(dateKey)) {
                deliveryDateMap.set(dateKey, {
                  date: dateKey,
                  producers: [],
                  total_amount: 0
                });
              }
              
              const dateSales = deliveryDateMap.get(dateKey)!;
              dateSales.total_amount += revenue;
              
              const existingProducer = dateSales.producers.find(p => p.producer_id === producerId);
              if (existingProducer) {
                existingProducer.total_amount += revenue;
              } else {
                dateSales.producers.push({
                  producer_id: producerId,
                  producer_name: producer.producer_name,
                  total_amount: revenue
                });
              }
            }
          }
        });
      }
      
      // After all items are processed, set the correct product list for each producer
      producerProductsMap.forEach((product, key) => {
        const [producerId] = key.split(':');
        const producer = producerOrdersData.get(producerId);
        if (producer) {
          producer.products.push(product);
        }
      });
      
      // Calculate total revenue from orders
      const totalRevenue = orders ? orders.reduce((sum, order) => sum + order.total_amount, 0) : 0;
      
      // Sort delivery dates by next upcoming dates first, then past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to beginning of day for comparison
      
      const sortedDeliveryDates = Array.from(deliveryDateMap.values())
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          
          // Both dates are in the future
          if (dateA >= today && dateB >= today) {
            return dateA.getTime() - dateB.getTime(); // Ascending order for future dates
          }
          // Both dates are in the past
          else if (dateA < today && dateB < today) {
            return dateB.getTime() - dateA.getTime(); // Most recent past dates first
          }
          // One is future, one is past - prioritize future
          else {
            return dateA >= today ? -1 : 1;
          }
        });
      
      // Create array of available dates for filtering, sorted by nearest future date first
      const formattedDates = Array.from(allDeliveryDates)
        .sort((a, b) => {
          const dateA = new Date(a);
          const dateB = new Date(b);
          
          // Both dates are in the future
          if (dateA >= today && dateB >= today) {
            return dateA.getTime() - dateB.getTime(); // Ascending order for future dates
          }
          // Both dates are in the past
          else if (dateA < today && dateB < today) {
            return dateB.getTime() - dateA.getTime(); // Most recent past dates first
          }
          // One is future, one is past - prioritize future
          else {
            return dateA >= today ? -1 : 1;
          }
        });
      
      setAvailableDates(formattedDates);
      
      // Set the selected date to the first upcoming delivery date if available
      if (formattedDates.length > 0) {
        const upcomingDates = formattedDates.filter(date => new Date(date) >= today);
        if (upcomingDates.length > 0) {
          setSelectedDate(upcomingDates[0]);
        }
      }
      
      // Update state with real data
      setStats({
        totalProducts: productsCount || 0,
        totalProducers: producers?.length || 0,
        totalOrders: orders?.length || 0, 
        totalRevenue,
        producerOrders: Array.from(producerOrdersData.values())
          .filter(producer => producer.products.length > 0)
          .sort((a, b) => b.total_revenue - a.total_revenue),
        salesByDeliveryDate: sortedDeliveryDates
      });
      
      logger.debug("Dashboard stats loaded successfully");
    } catch (err) {
      logger.error('Error fetching stats:', err);
      setError('Failed to load dashboard data');
      setErrorDetails(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  React.useEffect(() => {
    fetchStats();

    // Subscribe to changes in the orders table
    const subscription = supabase
      .channel('orders-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'orders' 
        }, 
        () => {
          logger.debug('Orders table change detected, refreshing dashboard stats...');
          fetchStats();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchStats]);

  // Filter products by selected delivery date
  const getFilteredProducts = (producer: ProducerOrdersInfo, date: string | 'all') => {
    if (date === 'all') {
      return producer.products;
    }
    
    return producer.products
      .filter(product => product.delivery_dates && product.delivery_dates.includes(date))
      .map(product => {
        // Make a copy with safe access to date-specific data
        return {
          ...product,
          quantity: (product.quantities_by_date && product.quantities_by_date[date]) || 0,
          total: (product.totals_by_date && product.totals_by_date[date]) || 0
        };
      });
  };
  
  // Calculate total revenue for filtered products
  const getFilteredTotal = (products: ProductSale[]) => {
    return products.reduce((sum, product) => sum + product.total, 0);
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="mt-4 h-8 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex flex-col">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            {errorDetails && (
              <div className="mt-2 text-xs text-red-500 overflow-auto max-h-32 bg-red-50 p-2 rounded border border-red-200">
                <pre>{JSON.stringify(errorDetails, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Översikt</h1>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Totalt antal produkter
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {stats.totalProducts}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Totalt antal producenter
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {stats.totalProducers}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Totalt antal beställningar
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {stats.totalOrders}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total omsättning
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {stats.totalRevenue.toLocaleString('sv-SE')} Kr
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders by Delivery Date Section */}
      <div className="mt-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Beställningar per leveransdatum
            </h3>
            <div className="mt-4">
              {stats.salesByDeliveryDate.length > 0 ? (
                <div className="space-y-6">
                  {stats.salesByDeliveryDate.map((dateSales, index) => {
                    const deliveryDate = new Date(dateSales.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isUpcoming = deliveryDate >= today;
                    const isNext = index === 0 && isUpcoming;
                    
                    return (
                      <div 
                        key={dateSales.date} 
                        className={`border rounded-lg p-4 ${isNext ? 'border-2 border-green-500' : ''}`}
                      >
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">
                              Leveransdatum: {new Date(dateSales.date).toLocaleDateString('sv-SE')}
                              {isNext && <span className="ml-2 text-sm bg-green-100 text-green-800 py-1 px-2 rounded font-medium">Nästa leverans</span>}
                              {isUpcoming && !isNext && <span className="ml-2 text-sm bg-blue-100 text-blue-800 py-1 px-2 rounded font-medium">Kommande</span>}
                              {!isUpcoming && <span className="ml-2 text-sm bg-gray-100 text-gray-800 py-1 px-2 rounded font-medium">Passerad</span>}
                            </h4>
                          </div>
                          <div className="text-sm font-bold text-gray-900">
                            Totalt: {dateSales.total_amount.toLocaleString('sv-SE')} Kr
                          </div>
                        </div>
                        
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Producent
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Försäljning
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {dateSales.producers
                                .sort((a, b) => b.total_amount - a.total_amount)
                                .map((producer) => (
                                <tr key={producer.producer_id}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {producer.producer_name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {producer.total_amount.toLocaleString('sv-SE')} Kr
                                    <span className="ml-2 text-xs text-gray-500">
                                      ({((producer.total_amount / dateSales.total_amount) * 100).toFixed(1)}%)
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500">Inga beställningar för leverans</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Producer Orders Section with Date Filter */}
      <div className="mt-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Beställningar per producent
              </h3>
              
              {/* Delivery date filter */}
              <div className="flex items-center">
                <label htmlFor="delivery-date-filter" className="mr-2 text-sm font-medium text-gray-700">
                  Filtrera på leveransdatum:
                </label>
                <select
                  id="delivery-date-filter"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                >
                  <option value="all">Alla leveransdatum</option>
                  {availableDates.map(date => (
                    <option key={date} value={date}>
                      {new Date(date).toLocaleDateString('sv-SE')}
                      {date === availableDates[0] ? ' (Nästa leverans)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-4">
              {stats.producerOrders.length > 0 ? (
                <div className="space-y-6">
                  {stats.producerOrders.map((producer) => {
                    // Filter products based on selected date
                    const filteredProducts = getFilteredProducts(producer, selectedDate);
                    const filteredTotal = getFilteredTotal(filteredProducts);
                    
                    // Skip producers with no products for the selected date
                    if (selectedDate !== 'all' && filteredProducts.length === 0) {
                      return null;
                    }
                    
                    return (
                      <div key={producer.producer_id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-lg font-medium text-gray-900">{producer.producer_name}</h4>
                          <div className="text-sm text-gray-500">
                            {selectedDate === 'all' ? (
                              <span>Senaste beställning: {producer.last_order_date 
                                ? new Date(producer.last_order_date).toLocaleDateString() 
                                : 'Inga beställningar'}
                              </span>
                            ) : (
                              <span>Visar för leveransdatum: {new Date(selectedDate).toLocaleDateString('sv-SE')}</span>
                            )}
                          </div>
                        </div>
                        
                        {filteredProducts.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Produkt
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Antal
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Pris per styck
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Summa
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {filteredProducts.map((product) => (
                                  <tr key={product.product_id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {product.product_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {product.quantity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {product.price.toLocaleString('sv-SE')} Kr
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {product.total.toLocaleString('sv-SE')} Kr
                                    </td>
                                  </tr>
                                ))}
                                <tr className="bg-gray-50">
                                  <td colSpan={3} className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                                    Total {selectedDate !== 'all' ? 'för leveransdatum' : ''}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                    {filteredTotal.toLocaleString('sv-SE')} Kr
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500">Inga produkter sålda{selectedDate !== 'all' ? ' för detta leveransdatum' : ''}</p>
                        )}
                      </div>
                    );
                  }).filter(Boolean)} {/* Filter out null elements */}
                </div>
              ) : (
                <p className="text-gray-500">Inga producenter med beställningar</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}